import React, {useEffect} from 'react';
import {observer} from 'mobx-react';
import ModalSimple from '../../../../../global/modal-simple';
import {runInAction} from 'mobx';
import FormMultilineShipments from '../form-multiline-shipments';
import {buttonMultilineShipmentsStore} from './button-multiline-shipments-store';
import ButtonImportCsv from '../button-import-csv/button-import-csv';
import {checkout} from '../../../../../api';
import {ICartLineItem} from '../../../../i';

type Props = {
    cartItem: ICartLineItem;
};

function addressDomKey(cartItemId: number, addressIndex: number): string {
    return `cart-${cartItemId}-address-${addressIndex}`;
}

const ButtonMultilineShipments = observer(({cartItem}: Props) => {
    const modalId = `modal-multiline-shipments-${cartItem.id}`;

    useEffect(() => {
        const modal = document.getElementById(modalId);

        if (!modal) {
            return;
        }

        const observer = new MutationObserver(mutations => {
            mutations.forEach(async mutation => {
                if (mutation.attributeName === 'style' && modal.style.display === 'block') {
                    await checkout('get-cart');
                }
            });
        });

        observer.observe(modal, {attributes: true, attributeFilter: ['style']});

        return () => observer.disconnect();
    }, [modalId]);

    useEffect(() => {
        runInAction(() => {
            // approved ria
            buttonMultilineShipmentsStore.getAddressLines(cartItem);
        });
        // Keyed on id/qty deliberately. `cartItem` identity changes on every /api/checkout
        // response (api.ts replaces store.checkout wholesale), and getAddressLines can write
        // the address list and issue a save — depending on the object would re-fire this on
        // every round-trip and risk a save -> refresh -> save loop.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cartItem.id, cartItem.qty]);

    return (
        <div className="multiline-shipments-container">
            <ModalSimple
                id={modalId}
                title="Multiline Shipments"
                closeButtonText="Save & Close"
                saveAndClose={() => buttonMultilineShipmentsStore.saveAddress()}
                closeButtonDisabled={buttonMultilineShipmentsStore.saveButtonDisabled(cartItem.address)}
            >
                <div className="shipping-box">
                    {cartItem.address?.map((i, index) => {
                        const addressKey = addressDomKey(cartItem.id, index);
                        const addressNumber = index + 1;
                        const isExpanded = buttonMultilineShipmentsStore.expandedIndexes.includes(index);

                        return (
                            <div
                                key={index}
                                className="item-container"
                            >
                                <div className="item">
                                    <div>
                                        <span>Shipping Address {addressNumber}</span>
                                        {!isExpanded && <div className="address">{buttonMultilineShipmentsStore.address(i)}</div>}
                                    </div>
                                    {!isExpanded && (
                                        <button
                                            type="button"
                                            className="change"
                                            name={`change-${addressKey}`}
                                            aria-label={`Change shipping address ${addressNumber}`}
                                            onClick={() => buttonMultilineShipmentsStore.expandForm(index)}
                                        >
                                            Change
                                        </button>
                                    )}
                                </div>
                                {isExpanded && (
                                    <>
                                        <FormMultilineShipments
                                            address={i}
                                            idSuffix={addressKey}
                                        />
                                        <div className="form-buttons">
                                            <button
                                                className="btn btn-primary btn-clear"
                                                type="button"
                                                name={`clear-${addressKey}`}
                                                aria-label={`Clear entered shipping address ${addressNumber}`}
                                                onClick={() => buttonMultilineShipmentsStore.clearAddress(cartItem, index)}
                                            >
                                                Clear Address
                                            </button>
                                            <button
                                                className="btn btn-primary btn-save"
                                                type="button"
                                                name={`save-${addressKey}`}
                                                aria-label={`Save entered shipping address ${addressNumber}`}
                                                disabled={buttonMultilineShipmentsStore.formInvalid(i)}
                                                onClick={() => buttonMultilineShipmentsStore.collapseForm(index)}
                                            >
                                                Save Address
                                            </button>
                                        </div>
                                    </>
                                )}
                            </div>
                        );
                    })}

                    <div className="shipping-bottom">
                        <ButtonImportCsv
                            cartItem={cartItem}
                            modalId={modalId}
                        />

                        {!buttonMultilineShipmentsStore.expandedIndexes.length && (
                            <button
                                type="button"
                                className="btn-checkout btn-checkout-white btn-shipping-white"
                                aria-label="Clear all entered shipping addresses"
                                onClick={() => buttonMultilineShipmentsStore.clearAllAddresses(cartItem)}
                            >
                                Clear All
                            </button>
                        )}
                    </div>
                </div>
            </ModalSimple>

            <button
                type="button"
                className="btn-checkout btn-checkout-white btn-multiline-shipments"
                aria-label="Multiline Shipments"
                onClick={() => buttonMultilineShipmentsStore.openModal(cartItem.id)}
            >
                Multiline Shipments
            </button>
        </div>
    );
});

export default ButtonMultilineShipments;
